import { apiClient } from '@/shared/api';
import { buildAuthHeaders } from '@/shared/http/authHeaders';
import { API_ENDPOINTS, APP_CONFIG } from '@/config';
import type { LoginResponse, PreLoginResponse, RefreshResponse } from '@/features/auth/types/auth.types';
import { tokenStorage } from '@/features/auth/utils/tokenStorage';

const REFRESH_TOKEN_KEY = APP_CONFIG.session.refreshTokenKey;

function isMockAuthEnabled(): boolean {
  return (
    import.meta.env.VITE_USE_MOCK_AUTH === 'true' ||
    // Allows reuse if the project ever runs under Next env conventions
    (import.meta.env as unknown as Record<string, string | undefined>).NEXT_PUBLIC_USE_MOCK_AUTH === 'true'
  );
}

function unwrapResponseData<T>(raw: unknown): T {
  // BFF commonly returns: { response, data: { response, data: T } }
  const r = raw as any;
  const candidate =
    r?.data?.data ?? // preferred nested shape
    r?.data; // fallback if already unwrapped

  if (candidate && typeof candidate === 'object' && 'data' in candidate) {
    return (candidate as any).data as T;
  }

  return candidate as T;
}

export const authService = {
  async preLogin(): Promise<PreLoginResponse> {

    const newHeaders = buildAuthHeaders({
      withFirebaseSession: true,
      withLocation: true,
    });

    const raw = await apiClient.post<any>(API_ENDPOINTS.auth.preLogin, { data: {} }, { headers: newHeaders });
    return {
      response: raw?.response ?? raw?.data?.response ?? 'OK',
      data: unwrapResponseData<PreLoginResponse['data']>(raw),
    };
  },

  async login(): Promise<LoginResponse> {

    const newHeaders = buildAuthHeaders({
      withFirebaseSession: true,
      withTempToken: true,
      withMfaToken: true,
      withLocation: true,
    });

    if (isMockAuthEnabled()) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const mockPayload = btoa(
            JSON.stringify({
              sub: '1773370393360440abb25-c310-4830-8931-0bcc50c9b787',
              type: 'access',
            })
          ).replace(/=/g, '');

          const mockToken = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${mockPayload}.mock_signature`;

          resolve({
            data: {
              access_token: mockToken,
              refresh_token: 'the_refresh_token',
              expires_in: 3600,
              token_type: 'Bearer',
            },
            response: 'OK',
          });
        }, 100);
      });
    }

    // Real login (default): call BFF endpoint
    const mfaToken = tokenStorage.getMfaTokenOptional();
    const raw = await apiClient.post<any>(
      API_ENDPOINTS.auth.login,
      { data: mfaToken ? { mfa_token: mfaToken } : {} },
      { headers: newHeaders }
    );
    return {
      response: raw?.response ?? raw?.data?.response ?? 'OK',
      data: unwrapResponseData<LoginResponse['data']>(raw),
    };
  },

  async logout(): Promise<void> {
    return apiClient.post(API_ENDPOINTS.auth.logout);
  },

  async refresh(): Promise<RefreshResponse> {
    const refreshToken = tokenStorage.getRefreshTokenOptional() ?? sessionStorage.getItem(REFRESH_TOKEN_KEY);
    return apiClient.post<RefreshResponse>(API_ENDPOINTS.auth.refreshToken, { refreshToken });
  },
};
